/**
 * Resolve App Store metadata from the admin's browser.
 *
 * Apple throttles the Search API per source IP and rejects Cloudflare's shared
 * egress with 403, so the lookup has to run here rather than in the Worker.
 * Only the resulting mzstatic artwork URL is handed to the Worker for caching.
 *
 * Apple's lookup endpoint answers per storefront, so an app that ships only in,
 * say, Indonesia is invisible from the US storefront. We sweep every storefront
 * (highest-traffic first) and stop at the first hit.
 */

export type AppStoreApp = {
  appStoreAppID: string;
  appStoreURL: string;
  artworkURL: string;
  appName: string;
  /** Uppercase ISO code of the storefront that answered. */
  storefront: string;
};

export type AppStoreLookupOptions = {
  /** Progress ticks so the caller can show "checked 40 of 170 storefronts". */
  onProgress?: (checked: number, total: number) => void;
  signal?: AbortSignal;
};

type LookupResult = {
  results?: Array<{
    trackName?: string;
    trackViewUrl?: string;
    artworkUrl512?: string;
    artworkUrl100?: string;
  }>;
};

/** Every ISO code Apple runs an App Store storefront for. */
const ALL_STOREFRONTS = [
  "ae", "ag", "ai", "al", "am", "ao", "ar", "at", "au", "az", "bb", "be", "bf", "bg",
  "bh", "bj", "bm", "bn", "bo", "br", "bs", "bt", "bw", "by", "bz", "ca", "cd", "cg",
  "ch", "ci", "cl", "cm", "cn", "co", "cr", "cv", "cy", "cz", "de", "dk", "dm", "do",
  "dz", "ec", "ee", "eg", "es", "fi", "fj", "fm", "fr", "ga", "gb", "gd", "ge", "gh",
  "gm", "gr", "gt", "gw", "gy", "hk", "hn", "hr", "hu", "id", "ie", "il", "in", "iq",
  "is", "it", "jm", "jo", "jp", "ke", "kg", "kh", "kn", "kr", "kw", "ky", "kz", "la",
  "lb", "lc", "lk", "lr", "lt", "lu", "lv", "ly", "ma", "md", "me", "mg", "mk", "ml",
  "mm", "mn", "mo", "mr", "ms", "mt", "mu", "mv", "mw", "mx", "my", "mz", "na", "ne",
  "ng", "ni", "nl", "no", "np", "nz", "om", "pa", "pe", "pg", "ph", "pk", "pl", "pt",
  "pw", "py", "qa", "ro", "rs", "ru", "rw", "sa", "sb", "sc", "se", "sg", "si", "sk",
  "sl", "sn", "sr", "st", "sv", "sz", "tc", "td", "th", "tj", "tm", "tn", "to", "tr",
  "tt", "tw", "tz", "ua", "ug", "us", "uy", "uz", "vc", "ve", "vg", "vn", "vu", "ye",
  "za", "zm", "zw",
];

/** Checked right after the institution's own country so the common case is fast. */
const PRIORITY_STOREFRONTS = [
  "ph", "us", "sg", "my", "id", "th", "vn", "hk", "tw", "jp", "kr", "in", "au", "nz",
  "gb", "ae", "sa", "ca", "de", "fr", "es", "it", "nl", "br", "mx", "cn", "za", "tr",
];

/** Small enough that a full sweep stays under Apple's per-IP throttle. */
const CONCURRENCY = 6;

export function parseAppStoreID(from: string): string | null {
  const trimmed = from.trim();
  if (/^\d{5,20}$/.test(trimmed)) return trimmed;
  return trimmed.match(/\/id(\d{5,20})(?:[/?#]|$)/i)?.[1] ?? null;
}

export function isAppleArtworkURL(raw: string): boolean {
  try {
    const url = new URL(raw);
    return url.protocol === "https:" && /(^|\.)mzstatic\.com$/i.test(url.hostname);
  } catch {
    return false;
  }
}

/** Institution country first, then big markets, then the long tail. */
function searchOrder(countryCode: string): string[] {
  const preferred = /^[A-Za-z]{2}$/.test(countryCode) ? countryCode.toLowerCase() : "ph";
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const code of [preferred, ...PRIORITY_STOREFRONTS, ...ALL_STOREFRONTS]) {
    if (seen.has(code)) continue;
    seen.add(code);
    ordered.push(code);
  }
  return ordered;
}

type Probe =
  | { status: "found"; app: AppStoreApp }
  | { status: "missing" }
  | { status: "throttled" }
  | { status: "unreachable" };

async function probeStorefront(
  id: string,
  country: string,
  signal?: AbortSignal,
): Promise<Probe> {
  const endpoint =
    `https://itunes.apple.com/lookup?id=${encodeURIComponent(id)}` +
    `&country=${country}&entity=software`;

  let payload: LookupResult;
  try {
    const res = await fetch(endpoint, { signal });
    if (res.status === 403 || res.status === 429) return { status: "throttled" };
    if (!res.ok) return { status: "missing" };
    payload = (await res.json()) as LookupResult;
  } catch {
    return { status: "unreachable" };
  }

  const app = payload.results?.[0];
  const artworkURL = app?.artworkUrl512 ?? app?.artworkUrl100;
  if (!app || !artworkURL || !isAppleArtworkURL(artworkURL)) return { status: "missing" };

  return {
    status: "found",
    app: {
      appStoreAppID: id,
      appStoreURL: app.trackViewUrl ?? `https://apps.apple.com/${country}/app/id${id}`,
      artworkURL,
      appName: app.trackName ?? "",
      storefront: country.toUpperCase(),
    },
  };
}

export async function lookupAppStoreApp(
  idOrURL: string,
  countryCode: string,
  options: AppStoreLookupOptions = {},
): Promise<AppStoreApp> {
  const id = parseAppStoreID(idOrURL);
  if (!id) throw new Error("Enter a numeric App Store app ID or an apps.apple.com URL.");

  const storefronts = searchOrder(countryCode);
  let checked = 0;
  let throttled = false;
  let unreachable = 0;

  for (let start = 0; start < storefronts.length; start += CONCURRENCY) {
    if (options.signal?.aborted) throw new Error("App Store lookup cancelled.");

    const batch = storefronts.slice(start, start + CONCURRENCY);
    const probes = await Promise.all(
      batch.map((country) => probeStorefront(id, country, options.signal)),
    );

    checked += batch.length;
    options.onProgress?.(checked, storefronts.length);

    // Earliest storefront in the batch wins so the preferred country stays first.
    const hit = probes.find((probe) => probe.status === "found");
    if (hit?.status === "found") return hit.app;

    if (probes.some((probe) => probe.status === "throttled")) {
      throttled = true;
      break;
    }
    unreachable += probes.filter((probe) => probe.status === "unreachable").length;
    // Every request failing at the network layer means the browser can't reach Apple.
    if (unreachable === checked) {
      throw new Error("Could not reach the App Store lookup service from this browser.");
    }
  }

  if (throttled) {
    throw new Error(
      `Apple rate-limited the lookup after ${checked} storefronts. Wait a minute and try again.`,
    );
  }

  throw new Error(
    `App ID ${id} isn't live in any of the ${checked} App Store storefronts. ` +
      `Check https://apps.apple.com/app/id${id} — removed or unreleased apps stop resolving.`,
  );
}
